import { DestroyRef, Injectable, NgZone, inject, signal } from '@angular/core';
import {
  Event as RouterEvent,
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationStart,
  Router,
} from '@angular/router';

/** A navigation that settles faster than this never shows the bar (no flash on instant route changes). */
export const NAVIGATION_PROGRESS_SHOW_DELAY_MS = 150;
/** Once shown, the bar stays at least this long (no blink when a navigation ends right after the delay). */
export const NAVIGATION_PROGRESS_MIN_VISIBLE_MS = 400;

/**
 * Drives the global route-progress bar (`app-navigation-progress`): `visible`
 * is true while a router navigation is in flight — NavigationStart through
 * End / Cancel / Error / Skipped — which covers the async guard chain
 * (app-auth-guard: Teams detection → code exchange → auth state → users/{uid}),
 * resolvers and lazy chunk loads, all of which used to run with nothing on screen.
 *
 * Anti-flicker: shown only after NAVIGATION_PROGRESS_SHOW_DELAY_MS, then kept
 * for at least NAVIGATION_PROGRESS_MIN_VISIBLE_MS. A cancel that hands over to
 * another navigation (a guard's UrlTree redirect, or a newer navigation
 * superseding this one) keeps the current cycle running instead of restarting it.
 *
 * The FIRST navigation (the app starting up) does not show it while the boot
 * splash is up: the splash owns that moment, and a chip over an empty window
 * reads as a changed loading screen. If the splash lifts (its time ceiling)
 * while that navigation is still running, `bootLoaderGone()` hands it over to
 * the chip, so a slow cold load never shows a blank page.
 *
 * Same behaviour on every surface (web, desktop shell, mobile shell): the bar
 * is click-through and never blocks input, so it can't trap a back gesture.
 */
@Injectable({ providedIn: 'root' })
export class NavigationProgressService {
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);

  private readonly visibleSignal = signal(false);
  readonly visible = this.visibleSignal.asReadonly();

  private pending = false;
  private shownAt = 0;
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private started = false;
  /** True once the first (startup) navigation has settled; until then the chip stays hidden. */
  private initialSettled = false;

  /** Replaceable in tests. */
  now = (): number => Date.now();

  constructor() {
    inject(DestroyRef).onDestroy(() => this.clearTimers());
  }

  /** Idempotent: subscribes to the router once. */
  start(): void {
    if (this.started) return;
    this.started = true;
    const sub = this.router.events.subscribe((event) => this.onRouterEvent(event));
    this.unsubscribe = () => sub.unsubscribe();
  }

  private unsubscribe: (() => void) | null = null;

  /**
   * The index.html boot splash was removed. If the startup navigation is still in flight
   * (the splash hit its ceiling under a slow guard chain), show the chip for the rest of it.
   */
  bootLoaderGone(): void {
    if (this.initialSettled) return;
    this.initialSettled = true;
    this.begin();
  }

  /** Visible for tests. */
  onRouterEvent(event: RouterEvent): void {
    if (!this.initialSettled) {
      // Startup: the boot splash covers it. A guard redirect keeps the same startup cycle going.
      const handedOver = event instanceof NavigationCancel && handsOver(event);
      const settled = event instanceof NavigationEnd
        || event instanceof NavigationError
        || event instanceof NavigationSkipped
        || (event instanceof NavigationCancel && !handedOver);
      if (settled) this.initialSettled = true;
      return;
    }
    if (event instanceof NavigationStart) {
      this.begin();
    } else if (event instanceof NavigationCancel) {
      if (handsOver(event)) return;
      this.end();
    } else if (event instanceof NavigationEnd || event instanceof NavigationError || event instanceof NavigationSkipped) {
      this.end();
    }
  }

  private begin(): void {
    if (this.hideTimer) {
      // A new navigation while the minimum display time runs out: keep showing.
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    if (this.pending) return;
    this.pending = true;
    if (this.visibleSignal() || this.showTimer) return;
    this.showTimer = this.schedule(() => {
      this.showTimer = null;
      if (!this.pending) return;
      this.shownAt = this.now();
      this.visibleSignal.set(true);
    }, NAVIGATION_PROGRESS_SHOW_DELAY_MS);
  }

  private end(): void {
    this.pending = false;
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
    if (!this.visibleSignal() || this.hideTimer) return;
    const remaining = NAVIGATION_PROGRESS_MIN_VISIBLE_MS - (this.now() - this.shownAt);
    if (remaining <= 0) {
      this.visibleSignal.set(false);
      return;
    }
    this.hideTimer = this.schedule(() => {
      this.hideTimer = null;
      if (!this.pending) this.visibleSignal.set(false);
    }, remaining);
  }

  /** Timers run inside the zone so the (Eager) host re-renders when they flip `visible`. */
  private schedule(fn: () => void, ms: number): ReturnType<typeof setTimeout> {
    return this.zone.run(() => setTimeout(fn, ms));
  }

  private clearTimers(): void {
    if (this.showTimer) clearTimeout(this.showTimer);
    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.showTimer = this.hideTimer = null;
    this.unsubscribe?.();
    this.unsubscribe = null;
  }
}

/** A cancel immediately followed by another navigation: a guard redirect, or a newer navigation took over. */
function handsOver(event: NavigationCancel): boolean {
  return event.code === NavigationCancellationCode.Redirect
    || event.code === NavigationCancellationCode.SupersededByNewNavigation;
}
